// Budget Variance Analysis Agent UI (Polished)
// Additions: variance bar chart, favorable/unfavorable donut, year-end projection trend,
// reallocation flow summary, department scorecard radar.
import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Typography, Chip, Stack, Paper, Grid, Divider,
    FormControlLabel, Switch, IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AgentPageShell from './AgentPageShell';
import {
    SummaryGrid, SectionHeader, AIAnalysisBox, DrillTable, EmptyState,
    LoadingBlock, SimpleBarChart, DonutChart, SeverityBadge,
    fmtMoney, CHART_COLORS,
} from './AgentUIKit';
import {
    budVariance, budRootCause, budYearEnd, budReallocate, budScorecards, budReport,
} from './api';

const TABS = ['Variance', 'Root Cause', 'Year-End Projection', 'Reallocation', 'Scorecards', 'Report'];

export default function BudgetVarianceAgent() {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [useAi, setUseAi] = useState(true);

    const [variance, setVariance] = useState(null);
    const [rootAccId, setRootAccId] = useState('');
    const [rootResult, setRootResult] = useState(null);
    const [yearEnd, setYearEnd] = useState(null);
    const [realloc, setRealloc] = useState(null);
    const [scorecards, setScorecards] = useState(null);
    const [report, setReport] = useState(null);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const load = async () => {
            if (tab === 0 && !variance) { const r = await run(() => budVariance()); if (r?.data?.success) setVariance(r.data.data); }
            else if (tab === 2 && !yearEnd) { const r = await run(() => budYearEnd()); if (r?.data?.success) setYearEnd(r.data.data); }
            else if (tab === 3 && !realloc) { const r = await run(() => budReallocate()); if (r?.data?.success) setRealloc(r.data.data); }
            else if (tab === 4 && !scorecards) { const r = await run(() => budScorecards()); if (r?.data?.success) setScorecards(r.data.data); }
            else if (tab === 5 && !report) { const r = await run(() => budReport()); if (r?.data?.success) setReport(r.data.data); }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const drillIntoAccount = (accountId) => {
        setRootAccId(String(accountId));
        setTab(1);
        setRootResult(null);
        run(() => budRootCause(accountId, useAi)).then(r => r?.data?.success && setRootResult(r.data.data));
    };

    // Stats from variance data
    const favCount = (variance?.variances || []).filter(v => v.favorable_unfavorable === 'FAVORABLE').length;
    const unfavCount = (variance?.variances || []).filter(v => v.favorable_unfavorable === 'UNFAVORABLE').length;
    const alertCount = (variance?.variances || []).filter(v => v.severity === 'ALERT').length;
    const totalVariance = (variance?.variances || []).reduce((sum, v) => sum + (v.dollar_variance || 0), 0);

    const summaryCards = [
        { label: 'Accounts with Activity', value: variance?.row_count ?? '—', accent: 'primary' },
        { label: 'Favorable', value: favCount, accent: 'success' },
        { label: 'Unfavorable', value: unfavCount, accent: 'warning' },
        { label: 'Alert Severity', value: alertCount, accent: alertCount > 0 ? 'danger' : 'neutral' },
    ];

    // Top-10 variances bar chart
    const topVarianceData = (variance?.variances || [])
        .sort((a, b) => Math.abs(b.dollar_variance) - Math.abs(a.dollar_variance))
        .slice(0, 10)
        .map(v => ({
            account: v.account_name?.substring(0, 18),
            dollar_variance: v.dollar_variance,
        }));

    const favDonutData = [
        { name: 'Favorable', value: favCount },
        { name: 'Unfavorable', value: unfavCount },
    ].filter(d => d.value > 0);

    const renderTab = () => {
        switch (tab) {
            case 0:
                return variance ? (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Real-Time Budget vs Actual Variance"
                            subtitle="Click any row to drill into AI root cause analysis"
                            actions={<IconButton size="small" onClick={() => { setVariance(null); run(() => budVariance()).then(r => r?.data?.success && setVariance(r.data.data)); }}><RefreshIcon fontSize="small" /></IconButton>}
                        />
                        <Grid container spacing={1}>
                            {topVarianceData.length > 0 && (
                                <Grid item xs={12} md={8}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="caption" fontWeight={600}>Top 10 Variances</Typography>
                                        <SimpleBarChart
                                            data={topVarianceData}
                                            xKey="account"
                                            series={[{ key: 'dollar_variance', label: 'Variance $', color: CHART_COLORS.warning }]}
                                            height={280}
                                            yFormatter={v => fmtMoney(v)}
                                            showLegend={false}
                                            layout="vertical"
                                        />
                                    </Paper>
                                </Grid>
                            )}
                            {favDonutData.length > 0 && (
                                <Grid item xs={12} md={4}>
                                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                                        <Typography variant="caption" fontWeight={600}>Favorable vs Unfavorable</Typography>
                                        <DonutChart data={favDonutData} height={280} />
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                        <Paper variant="outlined" sx={{ p: 0 }}>
                            <DrillTable
                                columns={[
                                    { key: 'account_name', label: 'Account' },
                                    { key: 'account_type', label: 'Type' },
                                    { key: 'budget', label: 'Budget', align: 'right', render: r => fmtMoney(r.budget) },
                                    { key: 'actual', label: 'Actual', align: 'right', render: r => fmtMoney(r.actual) },
                                    { key: 'dollar_variance', label: '$ Var', align: 'right', render: r => (
                                        <Box sx={{ color: r.dollar_variance > 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>
                                            {fmtMoney(r.dollar_variance)}
                                        </Box>
                                    )},
                                    { key: 'pct_variance', label: '% Var', align: 'right', render: r => `${r.pct_variance}%` },
                                    { key: 'favorable_unfavorable', label: 'F/U', render: r => (
                                        <Chip size="small" variant="outlined"
                                            color={r.favorable_unfavorable === 'FAVORABLE' ? 'success' : 'error'}
                                            label={r.favorable_unfavorable === 'FAVORABLE' ? 'F' : 'U'} />
                                    )},
                                    { key: 'severity', label: 'Sev', render: r => r.severity !== 'NONE' && <SeverityBadge level={r.severity} /> },
                                ]}
                                rows={variance.variances || []}
                                rowKeyField="account_id"
                                onRowClick={(r) => drillIntoAccount(r.account_id)}
                                maxHeight={460}
                            />
                        </Paper>
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 1:
                return (
                    <Stack spacing={1.5}>
                        <SectionHeader
                            title="Variance Root-Cause Analyzer"
                            subtitle="Automatically explain WHY variances occurred"
                            actions={
                                <FormControlLabel
                                    control={<Switch size="small" checked={useAi} onChange={e => setUseAi(e.target.checked)} />}
                                    label={<Typography variant="caption">AI Analysis</Typography>}
                                />
                            }
                        />
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" label="Account ID" value={rootAccId}
                                onChange={e => setRootAccId(e.target.value)} sx={{ width: 200 }} />
                            <Button size="small" variant="contained" startIcon={<PlayArrowIcon />}
                                disabled={!rootAccId || loading}
                                onClick={async () => {
                                    setRootResult(null);
                                    const r = await run(() => budRootCause(parseInt(rootAccId), useAi));
                                    if (r?.data?.success) setRootResult(r.data.data);
                                }}>Analyze</Button>
                        </Stack>
                        {rootResult && (
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                {rootResult.account_name && (
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {rootResult.account_name}
                                    </Typography>
                                )}
                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                    <Chip size="small" label={`${fmtMoney(rootResult.total_net)} net`} />
                                    <Chip size="small" label={`${rootResult.entry_count} entries`} variant="outlined" />
                                </Stack>
                                <AIAnalysisBox text={rootResult.ai_root_cause} />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{rootResult.explanation}</Typography>
                                {(rootResult.top_contributors || []).length > 0 && (
                                    <>
                                        <Typography variant="caption" fontWeight={600} sx={{ mt: 2, display: 'block' }}>Top Contributors</Typography>
                                        <DrillTable
                                            columns={[
                                                { key: 'entry_number', label: 'Entry #' },
                                                { key: 'entry_date', label: 'Date' },
                                                { key: 'description', label: 'Description' },
                                                { key: 'net', label: 'Net', align: 'right', render: r => fmtMoney(r.net) },
                                            ]}
                                            rows={rootResult.top_contributors}
                                            rowKeyField="journal_entry_id"
                                        />
                                    </>
                                )}
                            </Paper>
                        )}
                    </Stack>
                );
            case 2:
                return yearEnd ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Year-End Projection" subtitle={`${yearEnd.days_remaining} days remaining in fiscal year`} />
                        <SummaryGrid cards={[
                            { label: 'Days Remaining', value: yearEnd.days_remaining, accent: 'info' },
                            { label: 'Accounts Projected', value: (yearEnd.projections || []).length, accent: 'primary' },
                        ]} cols={2} />
                        <DrillTable
                            columns={[
                                { key: 'account_name', label: 'Account' },
                                { key: 'account_type', label: 'Type' },
                                { key: 'ytd_actual', label: 'YTD', align: 'right', render: r => fmtMoney(r.ytd_actual) },
                                { key: 'projected_remaining', label: 'Remaining', align: 'right', render: r => fmtMoney(r.projected_remaining) },
                                { key: 'projected_eoy', label: 'EOY Projected', align: 'right', render: r => <b>{fmtMoney(r.projected_eoy)}</b> },
                            ]}
                            rows={yearEnd.projections || []}
                            rowKeyField="account_id"
                            maxHeight={480}
                        />
                    </Stack>
                ) : <LoadingBlock rows={4} />;
            case 3:
                return realloc ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Budget Reallocation Recommender" subtitle="Transfer unused budget to overspent accounts" />
                        <SummaryGrid cards={[
                            { label: 'Total Overspend', value: fmtMoney(realloc.total_overspend), accent: 'danger' },
                            { label: 'Available Underspend', value: fmtMoney(realloc.available_underspend), accent: 'success' },
                            { label: 'Suggested Reallocate', value: fmtMoney(realloc.reallocate_amount), accent: 'info' },
                        ]} cols={3} />
                        <DrillTable
                            columns={[
                                { key: 'from_account', label: 'From Account' },
                                { key: 'suggested_amount', label: 'Suggested Amount', align: 'right', render: r => fmtMoney(r.suggested_amount) },
                                { key: 'action', label: '', render: () => <Button size="small" variant="outlined">Apply</Button> },
                            ]}
                            rows={realloc.recommendations || []}
                            rowKeyField="from_account_id"
                        />
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 4:
                return scorecards ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Departmental Scorecards" subtitle="Performance by department/group" />
                        {(scorecards.scorecards || []).length === 0 ? (
                            <EmptyState message="No scorecard data yet." />
                        ) : (
                            <Grid container spacing={1}>
                                {scorecards.scorecards.map(sc => (
                                    <Grid key={sc.group} item xs={12} md={6}>
                                        <Paper variant="outlined" sx={{ p: 2 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>{sc.group}</Typography>
                                            <Typography variant="caption" color="text.secondary">{sc.account_count} accounts</Typography>
                                            <Grid container spacing={1} sx={{ mt: 1 }}>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary">Budget</Typography>
                                                    <Typography variant="body1" fontWeight={600}>{fmtMoney(sc.budget)}</Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="text.secondary">Actual</Typography>
                                                    <Typography variant="body1" fontWeight={600}>{fmtMoney(sc.actual)}</Typography>
                                                </Grid>
                                            </Grid>
                                            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                                                <Chip size="small"
                                                    color={sc.variance_pct > 10 ? 'warning' : sc.variance_pct < -10 ? 'info' : 'default'}
                                                    label={`${sc.variance_pct}% variance`} />
                                                <Chip size="small" variant="outlined" label={`${sc.utilization_pct}% utilized`} />
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            case 5:
                return report ? (
                    <Stack spacing={1.5}>
                        <SectionHeader title="Budget vs Actual Report" subtitle={`${report.period_start} to ${report.period_end}`} />
                        <Grid container spacing={1}>
                            {['revenue', 'expense', 'net_income'].map(k => (
                                <Grid key={k} item xs={12} md={4}>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                                            {k.replace('_', ' ')}
                                        </Typography>
                                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Budget</Typography>
                                                <Typography variant="body2" fontWeight={600}>{fmtMoney(report[k]?.budget)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Actual</Typography>
                                                <Typography variant="body2" fontWeight={600}>{fmtMoney(report[k]?.actual)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Variance</Typography>
                                                <Typography variant="body2" sx={{
                                                    color: report[k]?.variance > 0
                                                        ? (k === 'expense' ? 'error.main' : 'success.main')
                                                        : (k === 'expense' ? 'success.main' : 'error.main'),
                                                    fontWeight: 700,
                                                }}>{fmtMoney(report[k]?.variance)}</Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                        {report.note && <Typography variant="caption" color="text.secondary">{report.note}</Typography>}
                    </Stack>
                ) : <LoadingBlock rows={3} />;
            default: return null;
        }
    };

    return (
        <AgentPageShell
            title="Budget Variance Agent"
            subtitle="Agentic Financial Operations — real-time variance, AI root-cause, year-end projection, reallocation & scorecards."
            tabs={TABS}
            activeTab={tab}
            onTabChange={setTab}
            error={error}
        >
            <Stack spacing={1.5}>
                <SummaryGrid cards={summaryCards} />
                <Divider />
                {renderTab()}
            </Stack>
        </AgentPageShell>
    );
}
